export const WS_STORAGE_KEY = {
    STOP_RESET_PASSWORD: "stopresetpw",
    LANGUAGE_DIALOG_HIDDEN: "langprefdialog",
    KIOSK_NAME: "kioskname",
    KIOSK_DEVICE_ID: "kioskdeviceid",
};

export const DASHBOARD_SEARCH_RECENT = "ds_recent";

type LocalStorage = typeof window.localStorage;

interface WSStorage extends LocalStorage {
    HAS_STORAGE: boolean;
    setItem: (key: string, value: string | object, expiresMS?: number) => void;
    getKey: (key: string) => string;
    getAllKeys: () => string[];
}

/**
 * Shim around localStorage so people doing private browsing on certain versions
 * of Safari (or who otherwise don't have access to localStorage) don't get errors
 */
function WSStorage(useSessionStorage?: boolean) {
    const prefix = "__ws_";
    let storage: LocalStorage;
    let hasStorage = true;

    try {
        /* eslint-disable no-restricted-properties */
        storage = useSessionStorage ? window.sessionStorage : window.localStorage;
        storage.setItem("test", "1");
        storage.removeItem("test");
        /* eslint-enable */
    } catch (e) {
        hasStorage = false;
        storage = (function fakeStorage() {
            let data: { [key: string]: string } = {};

            return {
                getItem(key: string): string {
                    return data[key];
                },

                setItem(key: string, val: string): void {
                    data[key] = val;
                },

                removeItem(key: string): void {
                    delete data[key];
                },

                get length(): number {
                    return Object.keys(data).length;
                },

                clear(): void {
                    data = {};
                },

                key(): string {
                    throw new Error("not implemented");
                },
            };
        })();
    }

    const storageObj: WSStorage = {
        HAS_STORAGE: hasStorage,

        canExpire(key: string) {
            const val = storage.getItem(this.getKey(key));

            if (val && val[0] === "{") {
                const obj = JSON.parse(val) as { expires: number };

                return !!obj.expires;
            }

            return false;
        },

        getKey(key: string): string {
            return prefix + key;
        },

        getAllKeys(): string[] {
            return Object.keys(storage).filter((k) => k.match("^prefix"));
        },

        getItem(key: string) {
            const val = storage.getItem(this.getKey(key));

            if (val && val[0] === "{") {
                const obj = JSON.parse(val) as { value: string; expires: number };

                if (obj.expires && obj.expires <= new Date().getTime()) {
                    this.removeItem(key);
                    return null;
                }

                return obj.value;
            }

            return val;
        },

        setItem(key: string, value: string | object, expiresMS?: number) {
            const data: { value: string | object; expires?: number } = { value };
            if (expiresMS) {
                data.expires = new Date().getTime() + expiresMS;
            }

            return storage.setItem(this.getKey(key), JSON.stringify(data));
        },

        removeItem(key: string) {
            return storage.removeItem(this.getKey(key));
        },

        clear() {
            this.getAllKeys().forEach((key: string) => {
                storage.removeItem(key);
            });
        },

        /**
         * Use to convert old un-prefixed keys to new prefixed ones. Wow!
         */
        setLegacy(key: string) {
            if (hasStorage && storage[key]) {
                this.setItem(key, storage[key] as string);
                storage.removeItem(key);
            }
        },

        get length(): number {
            throw new Error("not implemented");
        },

        key(): string {
            throw new Error("not implemented");
        },
    };

    // Wipe out any expired keys on start-up
    storageObj.getAllKeys().forEach((key: string) => {
        storageObj.getItem(key);
    });

    return storageObj;
}

export default WSStorage();
export const wsSessionStorage = WSStorage(true);
